import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { get } from "@/api/client";
import { useLocations } from "@/api/hooks/useLocations";

type Recommendation = "keep" | "review" | "weed";

interface WeedingSuggestion {
  itemId: string;
  barcode: string;
  recordId: string;
  title: string;
  authors: string;
  publicationYear: number | null;
  callNumber: string | null;
  condition: string | null;
  compositeScore: number;
  recommendation: Recommendation;
  scores: {
    usage: number;
    recency: number;
    condition: number;
    duplicates: number;
  };
  contentAnalysis: {
    score: number;
    assessment: string;
    reasoning: string;
  } | null;
  statistics: {
    totalLoans: number;
    lastLoanDate: string | null;
  };
  reasons: string[];
}

interface WeedingStatistics {
  totalItems: number;
  weedingCandidates: number;
  neverBorrowed: number;
  notBorrowedIn3Years: number;
  poorCondition: number;
  weedingRate: number;
}

const RECOMMENDATION_STYLES: Record<Recommendation, { label: string; bg: string; text: string }> = {
  keep: {
    label: "Conserver",
    bg: "bg-green-100 dark:bg-green-900/30",
    text: "text-green-800 dark:text-green-200",
  },
  review: {
    label: "À examiner",
    bg: "bg-yellow-100 dark:bg-yellow-900/30",
    text: "text-yellow-800 dark:text-yellow-200",
  },
  weed: {
    label: "Désherber",
    bg: "bg-red-100 dark:bg-red-900/30",
    text: "text-red-800 dark:text-red-200",
  },
};

function ScoreBar({ score, label }: { score: number; label: string }) {
  const percentage = Math.round(score * 100);
  const color = score >= 0.7 ? "bg-red-500" : score >= 0.4 ? "bg-yellow-500" : "bg-green-500";

  return (
    <div className="flex items-center gap-2">
      <span className="w-20 text-xs text-gray-500 dark:text-gray-400">{label}</span>
      <div className="h-2 flex-1 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
        <div className={`h-full ${color}`} style={{ width: `${String(percentage)}%` }} />
      </div>
      <span className="w-8 text-xs text-gray-600 dark:text-gray-300">{percentage}%</span>
    </div>
  );
}

function SuggestionCard({ suggestion }: { suggestion: WeedingSuggestion }) {
  const [expanded, setExpanded] = useState(false);
  const style = RECOMMENDATION_STYLES[suggestion.recommendation];

  return (
    <div className="rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
      <div className="p-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span
                className={`rounded-full px-2 py-0.5 text-xs font-medium ${style.bg} ${style.text}`}
              >
                {style.label}
              </span>
              <span className="text-sm text-gray-500 dark:text-gray-400">
                Score: {Math.round(suggestion.compositeScore * 100)}%
              </span>
            </div>
            <h3 className="mt-2 font-medium text-gray-900 dark:text-white">{suggestion.title}</h3>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {suggestion.authors || "Auteur inconnu"} •{" "}
              {suggestion.publicationYear || "Année inconnue"}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {suggestion.barcode} • {suggestion.callNumber || "Sans cote"}
            </p>
          </div>
          <button
            onClick={() => {
              setExpanded(!expanded);
            }}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
          >
            {expanded ? "▲" : "▼"}
          </button>
        </div>

        {/* Reasons summary */}
        {suggestion.reasons.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1">
            {suggestion.reasons.map((reason, i) => (
              <span
                key={i}
                className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-700 dark:bg-gray-700 dark:text-gray-300"
              >
                {reason}
              </span>
            ))}
          </div>
        )}
      </div>

      {expanded && (
        <div className="space-y-4 border-t border-gray-200 p-4 dark:border-gray-700">
          {/* Score breakdown */}
          <div className="space-y-2">
            <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Détail des scores
            </h4>
            <ScoreBar score={suggestion.scores.usage} label="Usage" />
            <ScoreBar score={suggestion.scores.recency} label="Actualité" />
            <ScoreBar score={suggestion.scores.condition} label="État" />
            <ScoreBar score={suggestion.scores.duplicates} label="Doublons" />
          </div>

          {/* Statistics */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <span className="text-gray-500 dark:text-gray-400">Emprunts totaux:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {suggestion.statistics.totalLoans}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">Dernier emprunt:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {suggestion.statistics.lastLoanDate
                  ? new Date(suggestion.statistics.lastLoanDate).toLocaleDateString("fr-FR")
                  : "Jamais"}
              </span>
            </div>
            <div>
              <span className="text-gray-500 dark:text-gray-400">État physique:</span>
              <span className="ml-2 font-medium text-gray-900 dark:text-white">
                {suggestion.condition || "Non renseigné"}
              </span>
            </div>
          </div>

          {/* AI Analysis */}
          {suggestion.contentAnalysis && (
            <div className="rounded-lg bg-blue-50 p-3 dark:bg-blue-900/20">
              <h4 className="text-sm font-medium text-blue-900 dark:text-blue-100">
                🤖 Analyse IA du contenu
              </h4>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                Évaluation: <strong>{suggestion.contentAnalysis.assessment}</strong>
              </p>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                {suggestion.contentAnalysis.reasoning}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function WeedingPage() {
  const [selectedLocation, setSelectedLocation] = useState<string | null>(null);
  const [includeAI, setIncludeAI] = useState(true);
  const [filter, setFilter] = useState<"all" | "weed" | "review" | "keep">("all");

  const { data: locations } = useLocations();

  // Fetch statistics
  const { data: stats } = useQuery<{ statistics: WeedingStatistics }>({
    queryKey: ["weeding-stats", selectedLocation],
    queryFn: () =>
      get<{ statistics: WeedingStatistics }>(
        `/api/ai/weeding/locations/${String(selectedLocation)}/statistics`
      ),
    enabled: selectedLocation !== null,
  });

  // Fetch suggestions
  const { data, isLoading, error } = useQuery<{ suggestions: WeedingSuggestion[] }>({
    queryKey: ["weeding-suggestions", selectedLocation, includeAI],
    queryFn: () =>
      get<{ suggestions: WeedingSuggestion[] }>(
        `/api/ai/weeding/locations/${String(selectedLocation)}/suggestions?limit=50&includeAI=${String(includeAI)}`
      ),
    enabled: selectedLocation !== null,
  });

  const filteredSuggestions =
    filter === "all"
      ? (data?.suggestions ?? [])
      : (data?.suggestions ?? []).filter((s) => s.recommendation === filter);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-gray-900 dark:text-white">
          Désherbage intelligent
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Analyse IA des collections pour identifier les documents à retirer
        </p>
      </div>

      {/* Location selector */}
      <div className="flex flex-wrap items-center gap-4">
        <div className="min-w-[200px] flex-1">
          <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Établissement
          </label>
          <select
            value={selectedLocation ?? ""}
            onChange={(e) => {
              setSelectedLocation(e.target.value || null);
            }}
            className="w-full rounded-lg border border-gray-300 px-3 py-2 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
          >
            <option value="">Sélectionner un établissement...</option>
            {locations?.map((loc) => (
              <option key={loc.id} value={loc.id}>
                {loc.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="includeAI"
            checked={includeAI}
            onChange={(e) => {
              setIncludeAI(e.target.checked);
            }}
            className="rounded border-gray-300"
          />
          <label htmlFor="includeAI" className="text-sm text-gray-700 dark:text-gray-300">
            Analyse IA du contenu
          </label>
        </div>

        {selectedLocation && (
          <a
            href={`/api/ai/weeding/locations/${selectedLocation}/export?includeAI=${includeAI ? "true" : "false"}`}
            className="rounded-lg bg-gray-100 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-200 dark:hover:bg-gray-600"
          >
            📥 Exporter CSV
          </a>
        )}
      </div>

      {/* Statistics */}
      {stats && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-2xl font-bold text-gray-900 dark:text-white">
              {stats.statistics.totalItems}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Exemplaires</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-2xl font-bold text-red-600">{stats.statistics.weedingCandidates}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Candidats</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-2xl font-bold text-orange-600">{stats.statistics.neverBorrowed}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Jamais empruntés</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-2xl font-bold text-yellow-600">
              {stats.statistics.notBorrowedIn3Years}
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Inactifs 3+ ans</p>
          </div>
          <div className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
            <p className="text-2xl font-bold text-purple-600">{stats.statistics.poorCondition}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400">Mauvais état</p>
          </div>
        </div>
      )}

      {/* Filter tabs */}
      {selectedLocation && (
        <div className="flex gap-2">
          {(
            [
              { value: "all", label: "Tout" },
              { value: "weed", label: "🔴 À désherber" },
              { value: "review", label: "🟡 À examiner" },
              { value: "keep", label: "🟢 Conserver" },
            ] as const
          ).map((option) => (
            <button
              key={option.value}
              onClick={() => {
                setFilter(option.value);
              }}
              className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
                filter === option.value
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}

      {/* Content */}
      {!selectedLocation ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <span className="text-4xl">🗑️</span>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Sélectionnez un établissement pour analyser sa collection
          </p>
        </div>
      ) : isLoading ? (
        <div className="flex h-32 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          Erreur lors du chargement des suggestions
        </div>
      ) : filteredSuggestions.length === 0 ? (
        <div className="rounded-xl border border-gray-200 bg-white p-12 text-center dark:border-gray-700 dark:bg-gray-800">
          <span className="text-4xl">✨</span>
          <p className="mt-4 text-gray-500 dark:text-gray-400">
            Aucune suggestion de désherbage pour ce filtre
          </p>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filteredSuggestions.length} suggestion(s)
          </p>
          {filteredSuggestions.map((suggestion) => (
            <SuggestionCard key={suggestion.itemId} suggestion={suggestion} />
          ))}
        </div>
      )}
    </div>
  );
}
